import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  FileText,
  Save,
  Download,
  Eye,
  Loader2,
  Users,
  Calendar,
  CheckCircle,
  Edit,
  RefreshCw,
  Wand2,
  Settings2,
  FileSignature,
  ListChecks,
  RotateCcw,
  ChevronRight,
  GripVertical,
  AlertCircle,
  Check,
  X,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catégories de clauses avec leurs couleurs
const CATEGORY_COLORS = {
  obligatoire: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300" },
  optionnel: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300" },
  specifique: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300" },
};

const CATEGORY_LABELS = {
  obligatoire: "Obligatoire",
  optionnel: "Optionnel",
  specifique: "Spécifique",
};

export default function ContractAdminPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [template, setTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("generator");
  
  // Generator state
  const [clauses, setClauses] = useState([]);
  const [values, setValues] = useState({});
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [generatedPreview, setGeneratedPreview] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Edit clause state
  const [editingClause, setEditingClause] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Edit values state
  const [showValuesDialog, setShowValuesDialog] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [templateRes, contractsRes, clausesRes, valuesRes] = await Promise.all([
        axios.get(`${API_URL}/api/contract/template`, { headers }),
        axios.get(`${API_URL}/api/admin/contracts`, { headers }),
        axios.get(`${API_URL}/api/contract/clauses`, { headers }),
        axios.get(`${API_URL}/api/contract/clauses/values`, { headers })
      ]);
      
      setTemplate(templateRes.data);
      setContracts(contractsRes.data);
      setClauses(clausesRes.data);
      setValues(valuesRes.data);
      setEditedValues(valuesRes.data);
      
      // Pre-select enabled clauses
      const enabledClauses = clausesRes.data
        .filter(c => c.enabled)
        .map(c => c.id);
      setSelectedClauses(enabledClauses);
      
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleClause = async (clauseId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/contract/clauses/toggle/${clauseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setClauses(prev => prev.map(c => 
        c.id === clauseId ? { ...c, enabled: !c.enabled } : c
      ));
      
      // Update selected clauses
      setSelectedClauses(prev => {
        if (prev.includes(clauseId)) {
          return prev.filter(id => id !== clauseId);
        } else {
          return [...prev, clauseId];
        }
      });
      
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };
  
  const handleEditClause = (clause) => {
    setEditingClause({ ...clause });
    setShowEditDialog(true);
  };
  
  const handleSaveClause = async () => {
    if (!editingClause) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/contract/clauses/${editingClause.id}`,
        editingClause,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Clause mise à jour");
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveValues = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/contract/clauses/values`,
        editedValues,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setValues(editedValues);
      toast.success("Valeurs mises à jour");
      setShowValuesDialog(false);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  const handleGeneratePreview = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/contract/generate`,
        { clauses: selectedClauses, custom_values: values },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedPreview(response.data.content);
      setShowPreview(true);
    } catch (error) {
      toast.error("Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };
  
  const handleGenerateAndSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/contract/generate-and-save`,
        { clauses: selectedClauses, custom_values: values },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Contrat v${response.data.version} généré et sauvegardé!`);
      setShowPreview(false);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  const handleResetClauses = async () => {
    if (!window.confirm("Réinitialiser toutes les clauses aux valeurs par défaut ?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/contract/clauses/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Clauses réinitialisées");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };
  
  const handleDownloadContract = async (signatureId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/contract/download/${signatureId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "contrat_adhesion.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };
  
  // Group clauses by category
  const clausesByCategory = clauses.reduce((acc, clause) => {
    const cat = clause.category || "optionnel";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(clause);
    return acc;
  }, {});
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="contract-admin-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
          <FileSignature className="w-6 h-6 text-primary" />
          Gestion des Contrats
        </h1>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Contrats signés</p>
                <p className="text-2xl font-bold text-primary">{contracts.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Version actuelle</p>
                <p className="text-2xl font-bold">{template?.version || "1.0"}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clauses actives</p>
                <p className="text-2xl font-bold">{clauses.filter(c => c.enabled).length}/{clauses.length}</p>
              </div>
              <ListChecks className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Dernière signature</p>
                <p className="text-lg font-bold">
                  {contracts.length > 0 
                    ? format(parseISO(contracts[0].signed_at), "dd/MM/yyyy", { locale: fr })
                    : "-"}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generator" className="flex items-center gap-2">
            <Wand2 className="w-4 h-4" />
            Générateur
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Aperçu Contrat
          </TabsTrigger>
          <TabsTrigger value="signatures" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Signatures ({contracts.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Generator Tab */}
        <TabsContent value="generator" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Clauses List */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ListChecks className="w-5 h-5" />
                        Points du Contrat
                      </CardTitle>
                      <CardDescription>
                        Sélectionnez et personnalisez les clauses à inclure
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleResetClauses}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="multiple" defaultValue={["obligatoire", "optionnel", "specifique"]} className="space-y-2">
                    {["obligatoire", "optionnel", "specifique"].map((category) => (
                      <AccordionItem key={category} value={category} className="border rounded-lg">
                        <AccordionTrigger className={`px-4 py-3 ${CATEGORY_COLORS[category].bg} rounded-t-lg hover:no-underline`}>
                          <div className="flex items-center gap-3">
                            <Badge className={`${CATEGORY_COLORS[category].bg} ${CATEGORY_COLORS[category].text} ${CATEGORY_COLORS[category].border}`}>
                              {CATEGORY_LABELS[category]}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {(clausesByCategory[category] || []).length} clause(s)
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="p-0">
                          <div className="divide-y">
                            {(clausesByCategory[category] || []).map((clause) => (
                              <div
                                key={clause.id}
                                className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                                  !clause.enabled ? "opacity-50" : ""
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <GripVertical className="w-4 h-4 text-gray-400" />
                                  <Switch
                                    checked={clause.enabled}
                                    onCheckedChange={() => handleToggleClause(clause.id)}
                                    disabled={category === "obligatoire" && clause.id === "header"}
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{clause.title}</p>
                                    <p className="text-xs text-muted-foreground line-clamp-1">
                                      {clause.content.substring(0, 100)}...
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {clause.editable && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditClause(clause)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
            
            {/* Right: Values & Actions */}
            <div className="space-y-4">
              {/* Values Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings2 className="w-5 h-5" />
                    Variables du Contrat
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">Cotisation:</div>
                    <div className="font-medium">{values.montant_cotisation}€</div>
                    
                    <div className="text-muted-foreground">Délai paiement:</div>
                    <div className="font-medium">{values.delai_paiement} jours</div>
                    
                    <div className="text-muted-foreground">Préavis:</div>
                    <div className="font-medium">{values.preavis_mois} mois</div>
                    
                    <div className="text-muted-foreground">Contrib. événement:</div>
                    <div className="font-medium">{values.contribution_evenement}€</div>
                  </div>
                  
                  <Separator />
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setShowValuesDialog(true)}
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    Modifier les valeurs
                  </Button>
                </CardContent>
              </Card>
              
              {/* Actions Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full" 
                    onClick={handleGeneratePreview}
                    disabled={generating}
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Aperçu du contrat
                  </Button>
                  
                  <Button 
                    className="w-full" 
                    variant="default"
                    onClick={handleGenerateAndSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4 mr-2" />
                    )}
                    Générer et Sauvegarder
                  </Button>
                  
                  <p className="text-xs text-muted-foreground text-center">
                    Cela créera une nouvelle version du contrat
                  </p>
                </CardContent>
              </Card>
              
              {/* Legend */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Légende</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-700 border-red-300">Obligatoire</Badge>
                    <span className="text-xs text-muted-foreground">Toujours inclus</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 border-blue-300">Optionnel</Badge>
                    <span className="text-xs text-muted-foreground">Au choix</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300">Spécifique</Badge>
                    <span className="text-xs text-muted-foreground">Clauses légales</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Preview Tab */}
        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Aperçu du Contrat Actuel (v{template?.version})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] border rounded-lg p-6 bg-white">
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{template?.content || "Aucun contrat configuré"}</ReactMarkdown>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Signatures Tab */}
        <TabsContent value="signatures">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Contrats Signés
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun contrat signé pour le moment</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Food Truck</TableHead>
                      <TableHead>Signataire</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Date de signature</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contracts.map((contract) => (
                      <TableRow key={contract.id}>
                        <TableCell className="font-medium">
                          {contract.food_truck_nom || "-"}
                        </TableCell>
                        <TableCell>
                          {contract.prenom_signataire} {contract.nom_signataire}
                          {contract.qualite_signataire && (
                            <span className="text-muted-foreground text-xs block">
                              {contract.qualite_signataire}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">v{contract.template_version}</Badge>
                        </TableCell>
                        <TableCell>
                          {format(parseISO(contract.signed_at), "dd/MM/yyyy à HH:mm", { locale: fr })}
                        </TableCell>
                        <TableCell className="text-right">
                          {contract.pdf_generated && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadContract(contract.id)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Edit Clause Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier la clause</DialogTitle>
          </DialogHeader>
          {editingClause && (
            <div className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={editingClause.title}
                  onChange={(e) => setEditingClause({ ...editingClause, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Contenu (Markdown)</Label>
                <Textarea
                  value={editingClause.content}
                  onChange={(e) => setEditingClause({ ...editingClause, content: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                />
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Variables disponibles:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.keys(values).map((key) => (
                    <Badge key={key} variant="outline" className="text-xs cursor-pointer"
                      onClick={() => {
                        const textarea = document.querySelector('textarea');
                        const cursorPos = textarea?.selectionStart || editingClause.content.length;
                        const newContent = editingClause.content.slice(0, cursorPos) + 
                          `{{${key}}}` + editingClause.content.slice(cursorPos);
                        setEditingClause({ ...editingClause, content: newContent });
                      }}
                    >
                      {`{{${key}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveClause} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Values Dialog */}
      <Dialog open={showValuesDialog} onOpenChange={setShowValuesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier les variables du contrat</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant cotisation (€)</Label>
                <Input
                  type="number"
                  value={editedValues.montant_cotisation || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, montant_cotisation: e.target.value })}
                />
              </div>
              <div>
                <Label>En lettres</Label>
                <Input
                  value={editedValues.montant_cotisation_lettres || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, montant_cotisation_lettres: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Délai paiement (jours)</Label>
                <Input
                  type="number"
                  value={editedValues.delai_paiement || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, delai_paiement: e.target.value })}
                />
              </div>
              <div>
                <Label>Préavis résiliation (mois)</Label>
                <Input
                  type="number"
                  value={editedValues.preavis_mois || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, preavis_mois: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contribution événement (€)</Label>
                <Input
                  type="number"
                  value={editedValues.contribution_evenement || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, contribution_evenement: e.target.value })}
                />
              </div>
              <div>
                <Label>Note minimum (/5)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editedValues.note_min || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, note_min: e.target.value })}
                />
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label>Adresse siège social</Label>
              <Input
                value={editedValues.adresse_siege || ""}
                onChange={(e) => setEditedValues({ ...editedValues, adresse_siege: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email RGPD</Label>
                <Input
                  value={editedValues.email_rgpd || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, email_rgpd: e.target.value })}
                />
              </div>
              <div>
                <Label>Juridiction</Label>
                <Input
                  value={editedValues.juridiction || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, juridiction: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Délai mise en demeure (jours)</Label>
                <Input
                  type="number"
                  value={editedValues.delai_mise_demeure || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, delai_mise_demeure: e.target.value })}
                />
              </div>
              <div>
                <Label>Délai résolution amiable (jours)</Label>
                <Input
                  type="number"
                  value={editedValues.delai_amiable || ""}
                  onChange={(e) => setEditedValues({ ...editedValues, delai_amiable: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Ville de signature</Label>
              <Input
                value={editedValues.ville_signature || ""}
                onChange={(e) => setEditedValues({ ...editedValues, ville_signature: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValuesDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveValues} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Generated Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Aperçu du contrat généré
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] border rounded-lg p-6 bg-white">
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{generatedPreview}</ReactMarkdown>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fermer
            </Button>
            <Button onClick={handleGenerateAndSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder comme nouveau contrat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
